import { Tag } from '../../../api/Tag';

interface AddOrSelectTagParams {
  newTagName: string;
  token: string | null;
  availableTags: Tag[];
  selectedTagIds: number[];
  createTag: (tagName: string) => Promise<Tag>;
}

interface AddOrSelectTagResult {
  action: 'noop' | 'selected-existing' | 'created';
  createdTag?: Tag;
  selectedTagId?: number;
  message?: string;
  isError?: boolean;
}

interface DeleteTagParams {
  tag: Tag;
  token: string | null;
  confirmDelete: () => boolean;
  deleteTag: (tagId: number) => Promise<void>;
}

interface DeleteTagResult {
  deletedTagId?: number;
  message?: string;
  isError?: boolean;
  shouldAbort: boolean;
}

export const normalizeTagName = (tagName: string) => tagName.trim();

export const findTagSuggestions = (availableTags: Tag[], query: string): Tag[] => {
  const normalizedQuery = normalizeTagName(query).toLowerCase();

  if (!normalizedQuery) {
    return [];
  }

  return availableTags.filter((tag) =>
    tag.name.toLowerCase().includes(normalizedQuery)
  );
};

export const findExistingTagByName = (availableTags: Tag[], tagName: string): Tag | undefined => {
  const normalizedTagName = normalizeTagName(tagName).toLowerCase();

  return availableTags.find(
    (tag) => tag.name.toLowerCase() === normalizedTagName
  );
};

export const shouldAddOrSelectTag = (tagName: string, token: string | null) =>
  Boolean(normalizeTagName(tagName) && token);

export const addOrSelectTag = async ({
  newTagName,
  token,
  availableTags,
  selectedTagIds,
  createTag,
}: AddOrSelectTagParams): Promise<AddOrSelectTagResult> => {
  const trimmedNewTagName = normalizeTagName(newTagName);

  if (!shouldAddOrSelectTag(trimmedNewTagName, token)) {
    return { action: 'noop' };
  }

  const existingTag = findExistingTagByName(availableTags, trimmedNewTagName);

  if (existingTag) {
    return {
      action: 'selected-existing',
      selectedTagId: selectedTagIds.includes(existingTag.id) ? undefined : existingTag.id,
      message: `Tag "${existingTag.name}" sélectionné.`,
      isError: false,
    };
  }

  try {
    const createdTag = await createTag(trimmedNewTagName);

    return {
      action: 'created',
      createdTag,
      selectedTagId: createdTag.id,
      message: `Tag "${createdTag.name}" créé.`,
      isError: false,
    };
  } catch (error: unknown) {
    return {
      action: 'noop',
      message: `Erreur lors de la création du tag: ${
        error instanceof Error ? error.message : 'Erreur inconnue'
      }`,
      isError: true,
    };
  }
};

export const shouldDeleteTag = (
  token: string | null,
  confirmDelete: () => boolean
) => Boolean(token) && confirmDelete();

export const deleteManagedTag = async ({
  tag,
  token,
  confirmDelete,
  deleteTag,
}: DeleteTagParams): Promise<DeleteTagResult> => {
  if (!shouldDeleteTag(token, confirmDelete)) {
    return { shouldAbort: true };
  }

  try {
    await deleteTag(tag.id);

    return {
      shouldAbort: false,
      deletedTagId: tag.id,
      message: `Tag "${tag.name}" supprimé.`,
      isError: false,
    };
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Erreur inconnue';

    return {
      shouldAbort: true,
      message: `Erreur lors de la suppression du tag: ${message}. Il est probablement utilisé par d'autres recettes.`,
      isError: true,
    };
  }
};

export const buildNextSelectedTagIds = (
  selectedTagIds: number[],
  tagId: number,
  isSelected: boolean
) => (isSelected
  ? selectedTagIds.filter((id) => id !== tagId)
  : [...selectedTagIds, tagId]);
